
import { Component, Vue } from 'vue-property-decorator'
import { useCultureSubsidyStore } from '@/stores/cultureSubsidy'

type FundingSource = 'wire' | 'free' | 'loyalty' | 'recycling' | 'empty'

@Component({ name: 'FundingPanel' })
export default class extends Vue {
  store = useCultureSubsidyStore()
  selected: FundingSource = 'wire'
  amount = 1000
  comment = ''
  emptyVisible = false
  emptyAmount = 0

  get settings() {
    return this.store.settings
  }

  get pockets() {
    return this.store.pockets
  }

  get sources() {
    return [
      { id: 'wire', icon: 'el-icon-bank-card', title: this.$t('cultureSubsidy.funding.wireTitle'), text: this.$t('cultureSubsidy.funding.wireCard') },
      { id: 'free', icon: 'el-icon-wallet', title: this.$t('cultureSubsidy.pockets.free'), text: this.$t('cultureSubsidy.funding.freeCard') },
      { id: 'loyalty', icon: 'el-icon-star-off', title: this.$t('cultureSubsidy.pockets.loyalty'), text: this.$t('cultureSubsidy.funding.loyaltyCard') },
      { id: 'recycling', icon: 'el-icon-refresh', title: this.$t('cultureSubsidy.pockets.recycling'), text: this.$t('cultureSubsidy.funding.recyclingCard') }
    ]
  }

  get currentMax(): number {
    if (!this.pockets) return 0
    if (this.selected === 'free') return this.pockets.free
    if (this.selected === 'loyalty') return this.pockets.loyalty
    if (this.selected === 'recycling') return this.pockets.recycling
    return 0
  }

  get currentSourceTitle(): string {
    return String(this.$t(`cultureSubsidy.funding.transferFrom`, { pocket: this.$t(`cultureSubsidy.pockets.${this.selected}`) }))
  }

  open(source: FundingSource): void {
    if (source === 'empty' && this.pockets) {
      this.emptyAmount = this.pockets.cultureReserve
      this.emptyVisible = true
      return
    }
    this.selected = source
  }

  copyRef(): void {
    if (!this.settings) return
    const reference = this.settings.wireReference
    const done = () => this.$message.success(this.$t('cultureSubsidy.funding.copied') as string)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(reference).then(done).catch(() => undefined)
      return
    }
    done()
  }

  async submitTransfer(): Promise<void> {
    if (this.selected === 'empty' || this.selected === 'wire') return
    await this.submit(this.selected)
  }

  async submit(kind: 'wire' | 'free' | 'loyalty' | 'recycling'): Promise<void> {
    try {
      await this.store.transfer(kind, { amount: this.amount, comment: this.comment })
      this.comment = ''
      this.$message.success(this.$t('cultureSubsidy.funding.success') as string)
    } catch (error) {
      this.$message.error((error as Error).message)
    }
  }

  async submitEmpty(): Promise<void> {
    try {
      await this.store.transfer('empty', { amount: this.emptyAmount })
      this.emptyVisible = false
      this.$message.success(this.$t('cultureSubsidy.funding.emptySuccess') as string)
    } catch (error) {
      this.$message.error((error as Error).message)
    }
  }
}
